/**
 * Legal documents. Each one becomes a route at its `path` (see `@/routes`).
 * A section's `id` is its anchor (the same in every language). Its `body` is a
 * list of blocks: a string is a paragraph, `{ lead, text }` a paragraph with a
 * bold opening, `{ list }` bullets and `{ table: { head, rows } }` a table.
 * Email addresses in the text become links.
 */
export const LEGAL_META = {
  eyebrow: 'Legal',
  updated: 'Version 1.0 · effective 4 November 2026',
  draft: null,
  tocTitle: 'On this page',
}

export const LEGAL_DOCS = [
  {
    path: '/privacy',
    title: 'Privacy Policy',
    intro:
      'How we collect, use and protect personal data when you use MeetNPlan. If the English and Arabic versions differ, the Arabic version prevails.',
    sections: [
      {
        id: 'who-we-are',
        title: 'Who we are',
        body: [
          'MeetNPlan is run by Code LTD, commercial registration 7025667853, VAT number 311024246500003, of Prince Sultan Bin Abdulaziz Road, As Sulimaniyah, Riyadh 12232, Saudi Arabia, short national address RHSB3135 ("we", "us"). For anything about your personal data, write to privacy@meetnplan.co. For anything else, write to hello@meetnplan.co or call 800 100 0018.',
        ],
      },
      {
        id: 'who-this-covers-and-our-role',
        title: 'Who this covers, and our role',
        body: [
          "This policy covers people who visit meetnplan.co, people who use the MeetNPlan app, guests invited to meetings, and visitors who sign in at a company's reception.",
          {
            list: [
              'Your company decides. The company that uses MeetNPlan decides why and how records about its staff, guests and visitors are used. It is the controller of that data, and we process it on its instructions. Its own privacy notice also applies to you.',
              'We decide. We are the controller for accounts and sign-in, security, billing, support, our website, and our own messages to customers.',
            ],
          },
        ],
      },
      {
        id: 'the-data-we-collect',
        title: 'The data we collect',
        body: [
          {
            table: {
              head: ['About', 'Data', 'Comes from', 'Controller'],
              rows: [
                [
                  'Account holders',
                  'Name, work email, role, account status, how you joined, password (stored only as a hash), photo, language and date settings, notification choices, phone number if you add one, your status for today, the time you were last active, which versions of our Terms and this policy you accepted and when, and whether you agreed to product news',
                  'You, your company, or Google if you sign in with Google',
                  'We for sign-in and security, your company for its staff records',
                ],
                [
                  'Google users',
                  'Name, email and picture. If you connect Google Calendar: a permission token, stored encrypted, your busy times, and the details of meetings we add to your calendar',
                  'Google, with your permission',
                  'We',
                ],
                [
                  'Meetings',
                  "Title, date, time, room or address (your device's location if you press Use my location), online meeting link, attendees, client name, and the reason given if it is declined or cancelled",
                  'The person who books',
                  'Your company',
                ],
                [
                  'Invited guests',
                  'Name, email, company, phone (the host must enter one for a client meeting or a meeting in a room), VIP mark, your answer to the invitation, and any note you send about rescheduling',
                  'The host who invited you, and you',
                  'Your company',
                ],
                [
                  'Visitors at reception',
                  'Name, phone, company, email if given, purpose of visit, host, sign-in and sign-out times, your signature at sign-in and sign-out, and the name and phone of people with you',
                  'You and the reception team',
                  'Your company',
                ],
                [
                  'Messages',
                  'Text, files, voice notes, reactions and read marks',
                  'People using Messages',
                  'Your company',
                ],
                [
                  'Billing',
                  "Plan, invoices (with your company's legal name, VAT number and address), amounts and payment status. Card and STC Pay details go straight from your browser to our payment provider, and we do not store them",
                  "Your company's admins",
                  'We',
                ],
                [
                  'Support',
                  'What you tell us in a support ticket or email',
                  'You',
                  'We',
                ],
                [
                  'Technical',
                  'Security records of sign-ins and actions. Each signed-in session keeps your IP address and browser until it ends. Our hosting providers process IP addresses, and our abuse limits keep only a scrambled form of them',
                  'Your device',
                  'We',
                ],
                [
                  'People who contact us',
                  'What you put in your email. The form on our website opens your own email app',
                  'You',
                  'We',
                ],
              ],
            },
          },
          {
            lead: 'What you must give.',
            text: 'We need your name and work email to create an account. A phone number is optional unless your company asks for it. If you do not give a company the visitor details it asks for, it may not be able to sign you in.',
          },
          {
            lead: 'Sensitive data.',
            text: 'We do not ask for health, religious or other sensitive data, or for any information about credit. Please do not put it in messages, notes or files. Reception teams must not use MeetNPlan to keep copies of ID cards or other official documents.',
          },
        ],
      },
      {
        id: 'why-we-use-it-and-on-what-legal-basis',
        title: 'Why we use it, and on what legal basis',
        body: [
          {
            table: {
              head: ['Purpose', 'Legal basis'],
              rows: [
                [
                  'Running the service: accounts, bookings, invitations, reminders, reception, messages and calendar sync',
                  'If you accepted our Terms, that agreement. Otherwise, our legitimate interest in providing accounts to your employer. For guests and visitors, the basis your company relies on, usually its legitimate interest in running its office safely, or your consent',
                ],
                [
                  'Keeping the service secure: stopping abuse, checking passwords against known breaches, and keeping records of important actions',
                  'Our legitimate interest in protecting people and the service',
                ],
                [
                  'Billing, tax and legal records',
                  'The agreement with the Customer, and our legal duties',
                ],
                [
                  'Support',
                  'The agreement, or our legitimate interest in helping users',
                ],
                [
                  'Product news and offers',
                  'Your separate consent, given by ticking a box or in your Profile. You can stop at any time, free, with one click in each message or in your Profile',
                ],
              ],
            },
          },
          'We do not sell personal data, we do not use it for advertising, and we do not use it for any purpose that does not fit why we collected it.',
        ],
      },
      {
        id: 'if-someone-else-gave-us-your-details',
        title: 'If someone else gave us your details',
        body: [
          'A host enters a guest\'s details, and colleagues can add you to a meeting. The invitation tells you who invited you and what the meeting is. It comes from "Host (Company) via MeetNPlan", and replies go to the host. When a company asks a visitor for a signature, the sheet states how long the record is kept.',
        ],
      },
      {
        id: 'who-can-see-your-data',
        title: 'Who can see your data',
        body: [
          {
            table: {
              head: ['Data', 'Who can see it'],
              rows: [
                [
                  'Name, email, photo, role, last active time, status for today, language, date and notification settings, whether Google Calendar is connected, and how and when you joined and who approved you',
                  'Approved colleagues in your company',
                ],
                [
                  'Meetings',
                  "A meeting with a room: title, client name and invitees' names and emails are visible to approved colleagues. A meeting with no room: the organizer, invitees, company admins and anyone whose booking it asks to replace. Reception sees only its type and times, and colleagues see only that you are busy. People invited can see each other's names and answers",
                ],
                ['Applicants waiting for approval', 'Company admins and HR'],
                ['Your phone number', 'You, company admins, HR and reception'],
                [
                  "A guest's phone number",
                  'The organizer, people invited to that meeting, company admins, and reception for meetings with a room',
                ],
                [
                  'Visit records',
                  "Reception. A host sees their own visitors. Signatures and companions are for reception only. A visitor's name and company also appear in the host's notifications, which company admins and our operators can read, and in the meeting chat or the host's reception chat",
                ],
                [
                  'Messages and files',
                  'The members of that chat. Chats with a team inbox, such as HR or reception, are shared with that team',
                ],
                [
                  'Our staff',
                  'A small number of authorized operators can see accounts, bookings, meeting history, notifications, invitations, invoices, support tickets and company activity to run the service. Our technical administrators can reach stored data when that is needed for support, security or the law. Their access is limited, and it is not used for any other purpose',
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'who-we-share-it-with',
        title: 'Who we share it with',
        body: [
          'We use these providers to run the service. They process data for us. We also share data when the law or an authority requires it.',
          {
            table: {
              head: ['Provider', 'What they receive', 'Why', 'Where'],
              rows: [
                [
                  'Supabase',
                  'All service data',
                  'Database, sign-in, file storage and server functions',
                  'Database and files in Frankfurt, Germany. Server functions may run in the Supabase region nearest the request',
                ],
                [
                  'Cloudflare',
                  'All web traffic, including IP addresses',
                  'Delivering the sites, bot checks, security and network error reports',
                  'Global network',
                ],
                [
                  'Resend',
                  'Recipient, subject, content and attachments of emails',
                  'Sending email',
                  'Tokyo, Japan. Resend is a United States company',
                ],
                [
                  'Google',
                  "Sign-in details. If you connect Calendar: meeting title, client name, room, colleagues' emails, and a description naming the organizer and the colleagues invited, with only the number of outside guests. Outside guests' emails are not sent to Google, and their names are not sent, except as the client name when the host gives no company. IP address when fonts load. Email sent to our own addresses",
                  'Sign-in, calendar sync, fonts and our email',
                  "Global, in Google's data centres",
                ],
                [
                  'Moyasar',
                  'Card or STC Pay details, amount and company reference',
                  'Taking payments',
                  'Saudi Arabia',
                ],
                [
                  'Have I Been Pwned',
                  'The first five characters of a scrambled form of a new password, never the password',
                  'Checking for known breaches',
                  "Global, through Cloudflare's network",
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'data-outside-the-kingdom',
        title: 'Data outside the Kingdom',
        body: [
          'Your data is stored in Germany, and some providers above work in other countries. We transfer data only for the purposes in this policy, and only to the providers listed above. You can ask us for details of these transfers.',
        ],
      },
      {
        id: 'how-long-we-keep-it',
        title: 'How long we keep it',
        body: [
          {
            table: {
              head: ['Data', 'How long'],
              rows: [
                [
                  'Visit records, signatures, guest phone numbers on meetings, and visitor thank-you emails',
                  '90 days by default. Your company can set 30 to 365 days',
                ],
                [
                  'Messages and files',
                  '730 days by default. Your company can set 30 to 3,650 days',
                ],
                [
                  'Meeting chats',
                  'Closed a day after the meeting, when the organizer can delete it, and a chat nobody wrote in is removed then. Otherwise moved to Archived 30 days after the meeting by default (your company can set 1 to 365 days), then deleted with other messages',
                ],
                [
                  'One-time sign-in codes',
                  '10 minutes, stored only in a scrambled form',
                ],
                ['Abuse-limit records', 'Deleted soon after they expire'],
                [
                  'Signed-in sessions, with IP address and browser',
                  'Until you sign out, sign out of all devices, or your account is deleted',
                ],
                [
                  "Our hosting providers' technical logs, which include IP addresses",
                  'Up to 7 days',
                ],
                [
                  'Bookings, rescheduling notes, meeting history and company activity',
                  'While your company uses MeetNPlan. Admins can clear past bookings. They are deleted with the company',
                ],
                ['Notifications', '1 year'],
                [
                  'Records of email sent to guests',
                  '90 days, or until the meeting has passed if that is later',
                ],
                ['Support tickets', '3 years after the ticket is closed'],
                [
                  'People who applied to join, were declined and were never admitted',
                  '90 days after they were declined',
                ],
                [
                  "A guest's request to stop a company's emails",
                  'While that company uses MeetNPlan, so the request keeps working. It is deleted with the company',
                ],
                [
                  'Your account',
                  'Until you delete it or your company removes you. Your login, your bookings, your notifications and your invitations are then deleted. Messages you sent and past records stay until their own period ends',
                ],
                [
                  'A company that deletes its organization',
                  'It can be restored for 30 days. Then its data, files and logins are deleted',
                ],
                [
                  'Record of a closed company: name, domains, paid totals and who deleted it',
                  "10 years, then the name, domains and figures are erased. The company's ID stays so it is never given to anyone else",
                ],
                ['Audit records', '2 years. They stay after a company closes'],
                [
                  'Free-trial record: trial key, company name, founder email and founder ID',
                  '3 years, to stop repeat free trials',
                ],
                [
                  'A workspace whose subscription ended',
                  "Stays read-only with its data until the company deletes it. We delete it 12 months after the subscription ended, after 30 days' notice to its admins. A free plan with no end date does not expire",
                ],
                [
                  'Invoices and payment records',
                  '10 years, including after a company closes. They cannot be deleted on request, because the law requires us to keep them',
                ],
                [
                  'Records of which versions of our Terms and Privacy Policy you accepted',
                  '10 years, and longer while that version is still current',
                ],
                ['Backups', 'Up to 7 days, then overwritten'],
              ],
            },
          },
          'When a period ends, we destroy the data or anonymize it, unless the law requires us to keep it. Deleting a record does not recall emails already sent.',
        ],
      },
      {
        id: 'your-rights',
        title: 'Your rights',
        body: [
          'Under the Personal Data Protection Law, you have the right to:',
          {
            list: [
              'be told how your data is collected and used, why, and on what legal basis;',
              'access your data and get a copy in a clear, readable form;',
              'have wrong or incomplete data corrected or updated, and restricted while we check it;',
              'ask us to destroy data no longer needed, subject to section 9 and what the law requires us to keep;',
              'withdraw your consent at any time by writing to us, without affecting what was done before;',
              'complain to the authority, and claim compensation for harm as the law provides.',
            ],
          },
        ],
      },
      {
        id: 'how-to-use-your-rights',
        title: 'How to use your rights',
        body: [
          {
            lead: 'In the app.',
            text: 'In your Profile you can change your phone, photo, language, password and notification choices and your choice about product news, download a copy of your data, sign out of all devices, disconnect Google Calendar, and delete your account. A founder must first hand the role to another admin, and a lone founder can only delete the workspace. The last admin must first make someone else an admin. To correct your name or email, write to us.',
          },
          {
            lead: 'By email.',
            text: 'Write to the address in section 1. It is free. We may ask you to confirm who you are. We act within 30 days, or up to 30 more if a request takes disproportionate effort or you make several, and we tell you why first. The copy from Profile, Download my data, covers what we hold under your email address, including your sign-in sessions. It names your files and says whether a signature is held, without copying them, and it leaves out company activity and audit records. For anything else, we prepare a copy on request.',
          },
          {
            lead: 'Staff, guests and visitors.',
            text: 'If your request is about records your company controls, please ask your company first. If you write to us, we will pass it to your company and help it reply.',
          },
        ],
      },
      {
        id: 'how-we-protect-it',
        title: 'How we protect it',
        body: [
          "Connections are encrypted in transit. Passwords are stored as hashes. Sign-in tokens last 15 minutes before they renew. Access is limited by role and by database rules, so one company cannot read another's records. Files are kept in private storage. Google Calendar permission tokens are stored encrypted. Our operators sign in with a password and an authenticator app. Messages are not end-to-end encrypted. No system is completely secure, so please keep your password private.",
        ],
      },
      {
        id: 'if-something-goes-wrong',
        title: 'If something goes wrong',
        body: [
          'Where we are the controller, we notify the Saudi Data and AI Authority (SDAIA) within 72 hours of becoming aware of a breach that may harm your data or you, or conflict with your rights or interests, and we tell you without undue delay what happened and what you can do. For data your company controls, we tell your company without undue delay, and it handles those notices.',
        ],
      },
      {
        id: 'automated-decisions',
        title: 'Automated decisions',
        body: [
          'We make no decisions about you solely by automated means that have a legal or similar effect. Some simple rules run by themselves. A priority booking request is refused if nobody answers in 20 minutes, and an unattended room can be released if your company turns that on. A room booking request that nobody approves is declined when the meeting is due to start.',
        ],
      },
      {
        id: 'messages-from-us',
        title: 'Messages from us',
        body: [
          "We send messages the service needs, such as invitations, reminders, billing notices and security alerts. You can manage many of these in Profile, but we still send those about your account's security and billing. We send news and offers only with your separate consent. Each one shows who sent it and has a link to stop with one click, free. Every meeting email to a guest has a link to stop that company's emails to that address. If the host later sends that guest an email from MeetNPlan, the app tells them it was not sent.",
        ],
      },
      {
        id: 'under-18',
        title: 'Under 18',
        body: [
          "MeetNPlan is for adults at work. Accounts are for people aged 18 or over. If a visitor is under 18, the company receiving them is responsible for any guardian's consent the law requires.",
        ],
      },
      {
        id: 'cookies-and-browser-storage',
        title: 'Cookies and browser storage',
        body: ['See our Cookie Policy.'],
      },
      {
        id: 'changes-to-this-policy',
        title: 'Changes to this policy',
        body: [
          'We will post changes here with a new version and date. We tell account holders before an important change takes effect.',
          {
            table: {
              head: ['Version', 'Date', 'Change'],
              rows: [
                [
                  '1.0',
                  '4 November 2026',
                  'First version under the Personal Data Protection Law',
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'contact-and-complaints',
        title: 'Contact and complaints',
        body: [
          'Please write to us first at the address in section 1. You can complain to the Saudi Data and AI Authority (SDAIA), at sdaia.gov.sa, within 90 days of the event or of learning of it. SDAIA may accept later complaints for good reason.',
        ],
      },
    ],
  },
  {
    path: '/terms',
    title: 'Terms of Service',
    intro:
      'The agreement between MeetNPlan and the companies that use it. If the English and Arabic versions differ, the Arabic version prevails.',
    sections: [
      {
        id: 'who-we-are',
        title: 'Who we are',
        body: [
          'MeetNPlan is provided by Code LTD, commercial registration 7025667853, VAT number 311024246500003, of Prince Sultan Bin Abdulaziz Road, As Sulimaniyah, Riyadh 12232, Saudi Arabia ("we", "us"). Contact: hello@meetnplan.co or 800 100 0018.',
        ],
      },
      {
        id: 'this-agreement',
        title: 'This agreement',
        body: [
          'These Terms are an agreement between us and the company that uses MeetNPlan ("Customer"). You accept them for the Customer when you tick the box that says so as you create a workspace or place an order. If you accept for a company, you confirm you are authorized to bind it. The Customer must ensure its users follow sections 5 and 6. These Terms do not bind guests or visitors.',
        ],
      },
      {
        id: 'the-service',
        title: 'The service',
        body: [
          'MeetNPlan is online software for booking meetings and rooms, inviting guests, signing in visitors at reception, messaging inside a company, and syncing with Google Calendar. What you get depends on your plan, as shown in your order and account. We provide it online, so no goods are delivered.',
        ],
      },
      {
        id: 'who-may-use-it',
        title: 'Who may use it',
        body: [
          'Users must be 18 or over. MeetNPlan is for businesses and organizations. Government agencies, and anyone holding government data, must not use it while it is hosted outside the Kingdom.',
        ],
      },
      {
        id: 'accounts',
        title: 'Accounts',
        body: [
          'People join a workspace by company email domain, company code, invitation or link, and an admin or HR approves every new member. The Customer manages its members and roles, and is responsible for what happens under its accounts. Keep your sign-in details private, and tell us at once if you think an account has been misused. The founder of a workspace can hand that role to another admin.',
        ],
      },
      {
        id: 'acceptable-use',
        title: 'Acceptable use',
        body: [
          'You must use the service lawfully and must not:',
          {
            list: [
              'try to reach data or accounts you are not allowed to see;',
              'attack, overload, test or disrupt the service, or get around its limits;',
              'copy, resell or reverse engineer it, except as the law allows;',
              'send spam, or anything unlawful, harmful or against public order;',
              'keep copies of ID cards or other official documents in it, or upload sensitive data it was not built for.',
            ],
          },
        ],
      },
      {
        id: 'customer-data',
        title: 'Customer data',
        body: [
          'The Customer owns the data it puts into MeetNPlan. We use it only to provide and protect the service, as these Terms and our Privacy Policy describe. The Customer is responsible for having a lawful basis for that data, and for telling its staff, guests and visitors how it is used.',
        ],
      },
      {
        id: 'data-processing',
        title: 'Data processing',
        body: [
          'For personal data the Customer controls, the Customer is the controller and we are its processor. We process the categories in section 3 of our Privacy Policy for the term of this agreement and the periods in section 16. We will:',
          {
            list: [
              "process it only on the Customer's instructions, which are these Terms and its use of the service, unless the law requires otherwise;",
              'keep it confidential, and limit access to people who need it;',
              'apply appropriate technical and organizational security;',
              'tell the Customer in writing of any breach of its instructions or of Saudi law;',
              'use only the sub-processors in our Privacy Policy, which the Customer accepts, each bound to equivalent protection. We tell the Customer 30 days before adding or changing one, and it may object within 14 days. If we cannot resolve an objection, the Customer may end the affected service and get a refund of unused prepaid fees;',
              'tell the Customer that Supabase, Cloudflare, Resend and Google are United States companies, so United States law could require them to disclose data;',
              'tell the Customer of any disclosure a law or authority requires, where we lawfully can. These disclosures need no consent from data subjects;',
              'tell the Customer without undue delay about a personal data breach, so it can meet its duties;',
              'help the Customer respond to data subjects and to the Saudi Data and AI Authority (SDAIA);',
              'delete the data as section 16 describes, unless the law requires us to keep it;',
              'give the Customer the information it reasonably needs to check we are meeting these duties, and let it or an independent assessor check.',
            ],
          },
          'The Customer instructs us, on its behalf, to process data outside the Kingdom as our Privacy Policy describes.',
        ],
      },
      {
        id: 'free-plans-and-trials',
        title: 'Free plans and trials',
        body: [
          'A free plan or trial lasts for the period shown in your account. Each company email domain or personal mailbox can use one free trial. When it ends, the workspace becomes read-only and you are not charged. A free plan shown with no end date continues until we tell you, at least 60 days before, that it will end.',
        ],
      },
      {
        id: 'paid-plans-and-prices',
        title: 'Paid plans and prices',
        body: [
          'Paid plans are billed monthly or yearly. The price, any add-ons, VAT and the total are shown before you pay. We add VAT at the current rate, now 15%, and send an invoice for each payment. You pay by bank transfer or another method we agree, or by card or STC Pay through our payment provider where the app offers it. A paid-plan contract is concluded, and your plan starts, when we confirm payment.',
          {
            lead: 'Renewal.',
            text: 'Your plan renews only when you pay for the next period. We remind you 7, 3 and 1 days before it ends. Auto-renewal applies only if we offer it and you choose it at checkout.',
          },
          {
            lead: 'Price changes.',
            text: "We give at least 30 days' notice of a new price. It applies from your next renewal and never to a period you have paid for.",
          },
        ],
      },
      {
        id: 'late-payment',
        title: 'Late payment',
        body: [
          "If a period ends unpaid, you have 7 days' grace with full access. The workspace then becomes read-only, and your data stays, until you pay. We charge no interest or penalty for late payment.",
        ],
      },
      {
        id: 'cancelling-and-refunds',
        title: 'Cancelling and refunds',
        body: [
          {
            lead: 'Your cancellation right.',
            text: 'You may cancel within 7 days of the contract date for a full refund, unless a member has used a paid-only feature, because the service has then been received. If we delay starting your plan by more than 15 days, except for force majeure, you may cancel and recover what you paid and the costs the delay caused.',
          },
          {
            lead: 'Cancelling later.',
            text: 'You can cancel at any time in Billing. Your plan then runs to the end of the paid period and stops, and you can undo this until then. If you choose to stop immediately, the workspace becomes read-only at once and the remaining period is not refunded.',
          },
          {
            lead: 'Changing plan.',
            text: "A new plan starts when its payment is confirmed, and the unused days of the old plan are credited against its price. If that credit is worth more than the new plan's first charge, we arrange the change with you. Add-on reductions apply from your next renewal.",
          },
          'We also refund where these Terms or the law say so, or where we charged you in error. Refunds go back to the original payment method where possible.',
        ],
      },
      {
        id: 'availability-and-support',
        title: 'Availability and support',
        body: [
          'We work to keep MeetNPlan available, and we do not promise it will be uninterrupted or free of errors. We may carry out maintenance, and we try to give notice of planned work. Support is by ticket for admins and by email for everyone. We aim for a first reply within 24 hours, or 4 hours on priority support. These are targets, not guarantees.',
        ],
      },
      {
        id: 'changes-to-the-service',
        title: 'Changes to the service',
        body: [
          'We may improve or change features. If a change seriously reduces what you paid for, we will tell you in advance, and you may end the affected plan and get a refund of unused prepaid fees.',
        ],
      },
      {
        id: 'other-services',
        title: 'Other services',
        body: [
          "Google sign-in and Google Calendar are optional and subject to Google's terms. We are not responsible for services we do not run, but this does not limit what we owe you for our own part.",
        ],
      },
      {
        id: 'ending-the-agreement-and-your-data',
        title: 'Ending the agreement, and your data',
        body: [
          {
            lead: 'By you.',
            text: 'Stop using the service and cancel at any time. To delete a workspace, the founder must first remove every other member and cancel any live subscription. You can restore a deleted workspace for 30 days. After that its data, files and logins are deleted, except what the law requires us to keep and the closed-company, free-trial and audit records described in our Privacy Policy. You may ask us for a copy of your data first.',
          },
          {
            lead: 'By us.',
            text: 'If you seriously breach these Terms, we will tell you and give you 14 days to put it right, and we may end the agreement without a court judgment if you do not. You agree that we may suspend access immediately, without a court order or further notice, only where that is needed to stop a security threat, unlawful use or serious harm. We will tell you why as soon as we can.',
          },
        ],
      },
      {
        id: 'intellectual-property',
        title: 'Intellectual property',
        body: [
          'We own MeetNPlan and its software, design and brand. You get a limited right to use it during the agreement. If you send us feedback, we may use it freely.',
        ],
      },
      {
        id: 'confidentiality',
        title: 'Confidentiality',
        body: [
          "Each party keeps the other's non-public information confidential and uses it only for this agreement, unless the law requires disclosure.",
        ],
      },
      {
        id: 'warranties',
        title: 'Warranties',
        body: [
          'We provide the service with reasonable skill and care. Apart from that, and from what the law requires, we give no other promise that it will suit a particular purpose.',
        ],
      },
      {
        id: 'liability',
        title: 'Liability',
        body: [
          'Each party is responsible for foreseeable harm that it causes in breach of this agreement. Our total liability for all claims in any 12 months is limited to the fees the Customer paid us in those 12 months, or SAR 1,000 if it paid nothing. This limit applies to failure or delay in performing this agreement.',
          'Nothing in these Terms limits or excludes liability for fraud, gross negligence or harmful acts, or any liability or right that the law does not allow to be limited.',
        ],
      },
      {
        id: 'events-beyond-control',
        title: 'Events beyond control',
        body: [
          'Neither party is liable for failing to perform because of an event outside its reasonable control that could not have been foreseen or avoided. If it lasts more than 30 days, either party may end the affected plan, and we will refund unused prepaid fees. Fees for unavailable periods are credited.',
        ],
      },
      {
        id: 'changes-to-these-terms',
        title: 'Changes to these Terms',
        body: [
          "We will give at least 30 days' notice of a change, by email or in the app, unless a law requires it sooner. If you do not agree, you may end the agreement before the change takes effect, and we will refund unused prepaid fees. If you keep using the service after that date, you accept the change.",
        ],
      },
      {
        id: 'complaints',
        title: 'Complaints',
        body: [
          'Send complaints to the address in section 1. We reply within 10 working days. You can also complain to the Ministry of Commerce.',
        ],
      },
      {
        id: 'law-and-disputes',
        title: 'Law and disputes',
        body: [
          'These Terms are governed by the laws of the Kingdom of Saudi Arabia. The parties will first try to settle a dispute by talking for 30 days. If they cannot, the competent courts in Riyadh decide it. Either party may seek urgent relief from them at any time.',
        ],
      },
      {
        id: 'general',
        title: 'General',
        body: [
          "The Arabic version prevails over any translation. We send notices by email or in the app. If part of these Terms is invalid, the rest stays in force. Neither party may transfer this agreement without the other's consent, except to a successor of its business with notice. Sections on confidentiality, liability, disputes and data keep applying after the agreement ends.",
        ],
      },
    ],
  },
  {
    path: '/cookies',
    title: 'Cookie Policy',
    intro:
      'What MeetNPlan saves in your browser, and why. If the English and Arabic versions differ, the Arabic version prevails.',
    sections: [
      {
        id: 'the-short-version',
        title: 'The short version',
        body: [
          "We do not use advertising cookies or track you across other websites. Our own code sets no cookies. Instead, it saves small settings in your browser's storage. Some of our providers may set their own cookies, for security or for Google sign-in. This policy covers meetnplan.co and app.meetnplan.co.",
        ],
      },
      {
        id: 'what-these-things-are',
        title: 'What these things are',
        body: [
          'A cookie is a small file a website saves in your browser. Browser storage does a similar job: session storage is cleared when you close the tab, and local storage stays until you or the site clears it.',
        ],
      },
      {
        id: 'our-website-meetnplan-co',
        title: 'Our website, meetnplan.co',
        body: [
          {
            table: {
              head: ['Name', 'Type', 'Why', 'How long'],
              rows: [
                [
                  'locale',
                  'Local storage',
                  'Remembers Arabic or English',
                  'Until you clear it',
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'the-app-app-meetnplan-co',
        title: 'The app, app.meetnplan.co',
        body: [
          'All of these are needed for the app to work or to keep a choice you made. None are used for advertising.',
          {
            table: {
              head: ['Purpose', 'Names', 'How long'],
              rows: [
                [
                  'Keeping you signed in',
                  'mnp-auth-customer, mnp-auth-platform',
                  "Until you close the tab or, with Remember me, until you sign out. mnp-auth-platform, our operators' console, is always tab only",
                ],
                [
                  'Remember me and recent accounts: your email and up to five recent addresses and company names on this device',
                  'mnp_remember, mnp_remembered_email, mnp_known_accounts',
                  'Local storage. Your email and recent accounts are removed when you untick Remember me, and mnp_remember keeps that choice. Recent accounts are ignored after 30 days',
                ],
                [
                  'Language and date style',
                  'mnp_lang, mnp_datesys, mnp_lang_chosen, mnp_lang_cover',
                  'Local storage for your choice, tab only for the rest',
                ],
                [
                  'Unsent work: drafts of meetings, messages and notices',
                  'mnp:schedule-draft:*, mnp:chat-draft:*, mnp:chat-outbox:*, mnp:notice-draft:*, mnp_signup_draft',
                  'Tab only, until sent or closed. Notice drafts stay in local storage until sent or you sign out',
                ],
                [
                  'Notices you have already seen',
                  'mnp_dismissed_announcements, mnp:popups:seen:*, mnp:popups:hidden, mnp:desk:snooze',
                  'Local storage or tab only',
                ],
                [
                  'Getting you to the right place during sign-in and setup',
                  'mnp_auth_landing, mnp_auth_bounce, mnp_founding_intent, pendingInvite, pendingPriority, mnp_setup_*, mnp_member_arrival, mnp_chunk_reload',
                  'Tab only',
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'other-companies',
        title: 'Other companies',
        body: [
          {
            table: {
              head: ['Provider', 'What happens', 'Why'],
              rows: [
                [
                  'Cloudflare',
                  'May set __cf_bm or cf_clearance. Turnstile runs a check on sign-in pages. On both sites it also collects network error reports, without setting cookies',
                  'Security and reliability',
                ],
                [
                  'Google',
                  "Google Sign-In uses Google's own cookies on Google's domains, if you choose it. Its script may also save a g_state cookie on app.meetnplan.co, to remember that you signed out or closed its prompt. Fonts load from Google on both sites, so Google receives your IP address",
                  'Sign-in and fonts',
                ],
                [
                  'Moyasar',
                  'Payment and bank security pages may set their own cookies when you pay',
                  'Taking payment securely',
                ],
              ],
            },
          },
        ],
      },
      {
        id: 'your-choices',
        title: 'Your choices',
        body: [
          'You can clear or block cookies and browser storage in your browser settings. If you block what the app needs, you may not be able to sign in. Signing out removes your session from this browser, while your remembered email and recent accounts stay until you untick Remember me. We do not ask for cookie consent, because these items only run the service you asked for. If we ever add advertising or cross-site tracking, we will ask for your consent first.',
        ],
      },
      {
        id: 'changes-and-contact',
        title: 'Changes and contact',
        body: [
          'We update this page when these items change. Questions go to the address in our Privacy Policy.',
        ],
      },
    ],
  },
]
